/**
 * PublicInvoice — PIN-gated invoice viewer, and the customer's own copy.
 *
 * Route: /public-invoice/:id
 * Access: public (no auth required)
 *
 * Flow:
 *   1. Customer opens the link from the WhatsApp message
 *   2. Enters the last 4 digits of their registered phone number
 *   3. get_public_invoice() verifies server-side (rate-limited here: 5
 *      attempts, 10-minute lockout)
 *   4. On success the invoice is rendered as a document they can keep
 *
 * Security:
 *   - Nothing is fetched before verification. The old build loaded the whole
 *     transaction and compared the PIN in the browser, so the invoice was on
 *     the wire before anyone typed anything.
 *   - A wrong PIN and a non-existent invoice return the same error, so the
 *     endpoint cannot be used to discover which ids are real.
 *   - Rate limiting is localStorage-based: a convenience against fat fingers,
 *     not a security boundary. The real check is in the database function.
 *
 * The rendered invoice is deliberately laid out as a sheet of A4 rather than a
 * web page: it is printed and filed. `@media print` below strips the chrome so
 * "Unduh PDF" (the browser's own print-to-PDF) produces a clean document with
 * no extra work and no PDF library in the bundle.
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Lock,
  ShieldCheck,
  AlertCircle,
  Loader2,
  Download,
  Building2,
  Phone,
  Mail,
} from 'lucide-react';
import {
  fetchPublicInvoice,
  type PublicInvoiceData,
} from '@/lib/api/publicInvoice';
import { formatIDR } from '@/lib/format';

// ── Rate limit helpers ────────────────────────────────────────────────────────
const RATE_LIMIT_ATTEMPTS = 5;
const RATE_LIMIT_WINDOW_MS = 10 * 60 * 1000; // 10 minutes

function getRateLimitKey(txId: string) {
  return `ophth_pin_attempts_${txId}`;
}

function getRateLimitState(txId: string): { attempts: number; lockedUntil: number | null } {
  try {
    const raw = localStorage.getItem(getRateLimitKey(txId));
    if (!raw) return { attempts: 0, lockedUntil: null };
    return JSON.parse(raw) as { attempts: number; lockedUntil: number | null };
  } catch {
    return { attempts: 0, lockedUntil: null };
  }
}

function recordFailedAttempt(txId: string) {
  const state = getRateLimitState(txId);
  state.attempts += 1;
  const lockedUntil =
    state.attempts >= RATE_LIMIT_ATTEMPTS ? Date.now() + RATE_LIMIT_WINDOW_MS : null;
  try {
    localStorage.setItem(getRateLimitKey(txId), JSON.stringify({ ...state, lockedUntil }));
  } catch {
    // Private browsing: the lockout is a courtesy, the server check is the gate.
  }
}

function isLocked(txId: string): boolean {
  const { lockedUntil } = getRateLimitState(txId);
  return lockedUntil !== null && Date.now() < lockedUntil;
}

function clearRateLimit(txId: string) {
  try {
    localStorage.removeItem(getRateLimitKey(txId));
  } catch {
    /* see recordFailedAttempt */
  }
}

function remainingAttempts(txId: string): number {
  return Math.max(0, RATE_LIMIT_ATTEMPTS - getRateLimitState(txId).attempts);
}

// ── Display helpers ───────────────────────────────────────────────────────────

/** "Ophthalindo Jaya Mandiri" → "OJM". Used when no logo is configured. */
function monogram(name: string): string {
  const letters = name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  return letters.slice(0, 3) || 'INV';
}

const STATUS_LABELS: Record<string, { label: string; tone: 'success' | 'warning' | 'danger' | 'info' }> = {
  paid: { label: 'LUNAS', tone: 'success' },
  pending_delivery: { label: 'MENUNGGU PENGIRIMAN', tone: 'warning' },
  unpaid: { label: 'BELUM LUNAS', tone: 'warning' },
  partial: { label: 'DIBAYAR SEBAGIAN', tone: 'warning' },
  overdue: { label: 'JATUH TEMPO', tone: 'danger' },
  partially_returned: { label: 'SEBAGIAN DIRETUR', tone: 'info' },
  returned: { label: 'DIRETUR', tone: 'info' },
  cancelled: { label: 'DIBATALKAN', tone: 'danger' },
};

const PAYMENT_LABELS: Record<string, string> = {
  cash: 'Tunai',
  qris: 'QRIS',
  transfer: 'Transfer bank',
  card: 'Kartu',
};

const longDateTime = (iso: string) =>
  new Date(iso).toLocaleString('id-ID', { dateStyle: 'long', timeStyle: 'short' });

const longDate = (iso: string) =>
  new Date(iso).toLocaleDateString('id-ID', { dateStyle: 'long' });

// ── Main component ────────────────────────────────────────────────────────────
export default function PublicInvoice() {
  const { id: rawId } = useParams<{ id: string }>();
  // Tolerate a stray "{{1}}" in front of the id. A WhatsApp template whose
  // dynamic-URL button was saved with the placeholder typed into the base URL
  // (instead of letting Meta append it) sends
  // /public-invoice/%7B%7B1%7D%7D<id>, and the customer would land on a dead
  // link. The prefix is never part of a real id, so dropping it is safe and
  // keeps already-delivered messages working.
  const transactionId = rawId?.replace(/^\{\{\d+\}\}/, '');
  const navigate = useNavigate();

  const [pin, setPin] = useState('');
  const [data, setData] = useState<PublicInvoiceData | null>(null);
  const [verifiedAt, setVerifiedAt] = useState<Date | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  const [lockCountdown, setLockCountdown] = useState(0);

  useEffect(() => {
    if (!transactionId) return;
    setLocked(isLocked(transactionId));
  }, [transactionId]);

  // ── Lock countdown timer ─────────────────────────────────────────────────
  useEffect(() => {
    if (!locked) return;
    const tick = () => {
      const stillLocked = isLocked(transactionId ?? '');
      setLocked(stillLocked);
      if (!stillLocked) {
        setLockCountdown(0);
        return;
      }
      const { lockedUntil } = getRateLimitState(transactionId ?? '');
      setLockCountdown(Math.ceil((lockedUntil! - Date.now()) / 1000));
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [locked, transactionId]);

  const handleVerify = useCallback(async () => {
    if (!transactionId) return;
    if (pin.length !== 4) {
      setError('Masukkan 4 digit terakhir nomor HP kamu.');
      return;
    }
    setVerifying(true);
    setError(null);
    try {
      const inv = await fetchPublicInvoice(transactionId, pin);
      setData(inv);
      setVerifiedAt(new Date());
      clearRateLimit(transactionId);
    } catch {
      recordFailedAttempt(transactionId);
      const remaining = remainingAttempts(transactionId);
      if (remaining === 0) {
        setLocked(true);
        setError('Terlalu banyak percobaan salah. Coba lagi dalam 10 menit.');
      } else {
        setError(`Kode salah. Sisa percobaan: ${remaining}`);
      }
      setPin('');
    } finally {
      setVerifying(false);
    }
  }, [pin, transactionId]);

  if (!transactionId) {
    return (
      <NotFound
        message="Link invoice tidak lengkap. Buka link yang dikirim lewat WhatsApp."
        onBack={() => navigate('/login')}
      />
    );
  }

  if (!data) {
    return (
      <PinScreen
        pin={pin}
        setPin={setPin}
        onVerify={handleVerify}
        error={error}
        locked={locked}
        lockCountdown={lockCountdown}
        verifying={verifying}
      />
    );
  }

  return <InvoiceDocument data={data} verifiedAt={verifiedAt ?? new Date()} />;
}

// ── The invoice itself ────────────────────────────────────────────────────────
function InvoiceDocument({
  data,
  verifiedAt,
}: {
  data: PublicInvoiceData;
  verifiedAt: Date;
}) {
  const co = data.company;
  const status = STATUS_LABELS[data.paymentStatus] ?? {
    label: data.paymentStatus.replace(/_/g, ' ').toUpperCase(),
    tone: 'info' as const,
  };
  const toneClass = {
    success: 'bg-success/10 text-success border-success/30',
    warning: 'bg-warning/10 text-warning border-warning/30',
    danger: 'bg-danger/10 text-danger border-danger/30',
    info: 'bg-primary/10 text-primary border-primary/30',
  }[status.tone];

  return (
    <div className="min-h-screen bg-canvas pubinv-root">
      <PrintStyles />

      {/* Action bar — screen only. */}
      <div className="pubinv-actions sticky top-0 z-10 bg-surface/95 backdrop-blur border-b border-line">
        <div className="max-w-[860px] mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-caption text-ink-subtle">Invoice</p>
            <p className="text-small font-mono font-medium text-ink truncate">
              {data.invoiceNumber}
            </p>
          </div>
          <div className="text-right shrink-0">
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-body font-medium text-white transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              <Download size={16} />
              Unduh PDF
            </button>
            <p className="text-caption text-ink-subtle mt-1">
              Pilih &ldquo;Save as PDF&rdquo; di dialog cetak
            </p>
          </div>
        </div>
      </div>

      <main className="max-w-[860px] mx-auto px-4 py-6 pubinv-main">
        <article className="pubinv-sheet bg-surface border border-line rounded-lg shadow-hover overflow-hidden">
          {/* ── Letterhead ─────────────────────────────────────────────── */}
          <header className="px-8 pt-8 pb-6 flex items-start justify-between gap-6 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              {co.logoUrl ? (
                <img
                  src={co.logoUrl}
                  alt={co.name}
                  className="w-14 h-14 rounded object-cover border border-line shrink-0"
                />
              ) : (
                <div className="w-14 h-14 rounded-lg bg-primary text-white flex items-center justify-center shrink-0 font-display font-semibold text-subhead tracking-tight">
                  {monogram(co.name)}
                </div>
              )}
              <div className="min-w-0">
                <h1 className="text-section font-display font-semibold text-ink leading-tight">
                  {co.name}
                </h1>
                {co.address && (
                  <p className="text-caption text-ink-subtle mt-1 whitespace-pre-line leading-snug max-w-[38ch]">
                    {co.address}
                  </p>
                )}
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-caption text-ink-subtle">
                  {co.phone && (
                    <span className="inline-flex items-center gap-1">
                      <Phone size={10} /> {co.phone}
                    </span>
                  )}
                  {co.email && (
                    <span className="inline-flex items-center gap-1">
                      <Mail size={10} /> {co.email}
                    </span>
                  )}
                </div>
                {co.npwp && (
                  <p className="text-caption text-ink-subtle mt-0.5">NPWP: {co.npwp}</p>
                )}
              </div>
            </div>

            <div className="text-right shrink-0">
              <p className="text-overline uppercase tracking-widest text-ink-subtle font-medium">
                Invoice
              </p>
              <p className="text-subhead font-display font-semibold text-ink font-mono mt-0.5">
                {data.invoiceNumber}
              </p>
              <p className="text-caption text-ink-subtle mt-0.5">
                {longDateTime(data.createdAt)}
              </p>
              <span
                className={`inline-block mt-2 rounded border px-2.5 py-1 text-caption font-semibold tracking-wide ${toneClass}`}
              >
                {status.label}
              </span>
            </div>
          </header>

          {/* ── Authenticity band ──────────────────────────────────────────
              Says only what is true: this document was served by the
              company's own system, and it opened because the reader knew the
              registered number. It is not a digital signature and does not
              claim to be one. */}
          <div className="mx-8 mb-6 rounded-lg border border-success/30 bg-success/5 px-4 py-3 flex items-start gap-3">
            <ShieldCheck size={20} className="text-success shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-small font-semibold text-ink">
                Dokumen asli diterbitkan oleh {co.name}
              </p>
              <p className="text-caption text-ink-subtle mt-0.5">
                Dibuka dengan nomor HP terdaftar dan diverifikasi langsung ke sistem{' '}
                {monogram(co.name)} pada {longDateTime(verifiedAt.toISOString())}.
              </p>
            </div>
          </div>

          {/* ── Parties ────────────────────────────────────────────────── */}
          <div className="px-8 pb-6 grid gap-6 sm:grid-cols-2 border-b border-line">
            <div>
              <p className="text-overline uppercase tracking-widest text-ink-subtle font-medium">
                Ditagihkan kepada
              </p>
              <p className="text-body font-medium text-ink mt-1.5">
                {data.customer?.name || 'Pelanggan'}
              </p>
              {data.customer?.company && (
                <p className="text-small text-ink-subtle">{data.customer.company}</p>
              )}
              {data.customer?.phone && (
                <p className="text-small text-ink-subtle font-mono">{data.customer.phone}</p>
              )}
            </div>
            <div className="sm:text-right">
              <p className="text-overline uppercase tracking-widest text-ink-subtle font-medium">
                Pembayaran
              </p>
              <p className="text-body font-medium text-ink mt-1.5">
                {PAYMENT_LABELS[data.paymentMethod] ?? data.paymentMethod?.toUpperCase() ?? '—'}
              </p>
              <p className="text-small text-ink-subtle">
                {data.fulfillment === 'later' ? 'Dikirim / diambil menyusul' : 'Barang diserahkan langsung'}
              </p>
              {data.dueDate && (
                <p className="text-small text-warning mt-0.5">
                  Jatuh tempo {longDate(data.dueDate)}
                </p>
              )}
            </div>
          </div>

          {/* ── Items ──────────────────────────────────────────────────── */}
          <table className="w-full">
            <thead>
              <tr className="border-b border-line">
                <th className="px-8 py-3 text-left text-overline uppercase tracking-widest text-ink-subtle font-medium">
                  Produk
                </th>
                <th className="px-2 py-3 text-right text-overline uppercase tracking-widest text-ink-subtle font-medium whitespace-nowrap">
                  Qty
                </th>
                <th className="px-2 py-3 text-right text-overline uppercase tracking-widest text-ink-subtle font-medium whitespace-nowrap">
                  Harga
                </th>
                <th className="px-8 py-3 text-right text-overline uppercase tracking-widest text-ink-subtle font-medium whitespace-nowrap">
                  Jumlah
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {data.items.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-8 py-6 text-center text-small text-ink-subtle">
                    Tidak ada item pada invoice ini.
                  </td>
                </tr>
              ) : (
                data.items.map((item, i) => (
                  <tr key={i} className="pubinv-row align-top">
                    <td className="px-8 py-3">
                      <p className="text-small font-medium text-ink">{item.name}</p>
                      {/* Lot and expiry belong on the customer's copy: this is
                          the document a recall or a shelf-life question is
                          answered from. */}
                      {(item.lotNumber || item.expiryDate) && (
                        <p className="text-caption text-ink-subtle mt-0.5 flex flex-wrap gap-x-3">
                          {item.lotNumber && (
                            <span>
                              Lot <span className="font-mono text-ink">{item.lotNumber}</span>
                            </span>
                          )}
                          {item.expiryDate && <span>ED {longDate(item.expiryDate)}</span>}
                        </p>
                      )}
                      {item.returnedQty > 0 && (
                        <p className="text-caption text-primary mt-0.5">
                          {item.returnedQty} diretur
                        </p>
                      )}
                      {(item.promoDiscount ?? 0) > 0 && (
                        <p className="text-caption text-success mt-0.5">Promo − {formatIDR(item.promoDiscount!)}</p>
                      )}
                    </td>
                    <td className="px-2 py-3 text-right text-small text-ink-subtle tabular-nums whitespace-nowrap">
                      {item.qty}
                      {item.unit ? ` ${item.unit}` : ''}
                    </td>
                    <td className="px-2 py-3 text-right text-small text-ink-subtle tabular-nums whitespace-nowrap">
                      {formatIDR(item.price)}
                    </td>
                    <td className="px-8 py-3 text-right text-small font-medium text-ink tabular-nums whitespace-nowrap">
                      {formatIDR(item.subtotal)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* ── Totals ─────────────────────────────────────────────────── */}
          <div className="border-t border-line px-8 py-5 flex justify-end">
            <dl className="w-full max-w-[300px] space-y-1.5">
              <TotalRow label="Subtotal" value={formatIDR(data.subtotal)} />
              {(data.appliedPromos ?? []).filter((p) => p.amount > 0).map((p) => (
                <TotalRow key={p.promo_id} label={p.name} value={`− ${formatIDR(p.amount)}`} tone="success" />
              ))}
              {(data.promoDiscount ?? 0) > 0 && (data.appliedPromos ?? []).length === 0 && (
                <TotalRow label="Promo" value={`− ${formatIDR(data.promoDiscount!)}`} tone="success" />
              )}
              {data.discountAmount > 0 && (
                <TotalRow label="Diskon" value={`− ${formatIDR(data.discountAmount)}`} tone="success" />
              )}
              {data.taxAmount > 0 && (
                <TotalRow
                  label={`PPN ${Number(data.taxRate ?? 0)}%`}
                  value={formatIDR(data.taxAmount)}
                />
              )}
              <div className="flex items-baseline justify-between gap-4 border-t border-line pt-2.5 mt-2.5">
                <dt className="text-body font-semibold text-ink">Total</dt>
                <dd className="text-subhead font-display font-semibold text-ink tabular-nums">
                  {formatIDR(data.grandTotal)}
                </dd>
              </div>
              {data.outstanding > 0 && (
                <>
                  <TotalRow label="Sudah dibayar" value={formatIDR(data.paidAmount)} />
                  <div className="flex items-baseline justify-between gap-4 rounded bg-warning/10 px-2 py-1.5 mt-1">
                    <dt className="text-small font-semibold text-warning">Sisa tagihan</dt>
                    <dd className="text-body font-semibold text-warning tabular-nums">
                      {formatIDR(data.outstanding)}
                    </dd>
                  </div>
                </>
              )}
            </dl>
          </div>

          {/* ── Footer ─────────────────────────────────────────────────── */}
          <footer className="border-t border-line bg-canvas/40 px-8 py-5 text-center">
            <p className="text-small text-ink">Terima kasih atas kepercayaan Anda.</p>
            <p className="text-caption text-ink-subtle mt-1">
              Ada pertanyaan tentang invoice ini? Hubungi {co.name}
              {co.phone ? ` di ${co.phone}` : ''}
              {co.email ? ` atau ${co.email}` : ''}.
            </p>
            <p className="text-caption text-ink-subtle/70 mt-2">
              Dokumen ini dibuat otomatis oleh sistem dan sah tanpa tanda tangan basah.
            </p>
          </footer>
        </article>

        <p className="pubinv-actions text-center text-caption text-ink-subtle mt-4">
          Simpan salinan ini untuk arsip Anda.
        </p>
      </main>
    </div>
  );
}

function TotalRow({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'success';
}) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-small text-ink-subtle">{label}</dt>
      <dd
        className={`text-small tabular-nums ${tone === 'success' ? 'text-success' : 'text-ink'}`}
      >
        {value}
      </dd>
    </div>
  );
}

/**
 * Print rules, scoped to this page.
 *
 * Kept next to the markup rather than in index.css: they exist only for this
 * one route, and the "Unduh PDF" button is only honest if what comes out of
 * the print dialog is a clean sheet rather than a screenshot of the app.
 */
function PrintStyles() {
  return (
    <style>{`
      @media print {
        @page { size: A4; margin: 14mm; }
        html, body { background: #fff !important; }
        /* Keep the tinted status chip, totals and authenticity band readable:
           browsers drop background colours in print unless told otherwise. */
        .pubinv-root, .pubinv-root * {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .pubinv-actions { display: none !important; }
        .pubinv-root { background: #fff !important; min-height: 0 !important; }
        .pubinv-main { max-width: none !important; padding: 0 !important; margin: 0 !important; }
        .pubinv-sheet {
          border: 0 !important;
          border-radius: 0 !important;
          box-shadow: none !important;
        }
        /* A line item must not be split across two pages — half a lot number
           on the next sheet is worse than a shorter page. */
        .pubinv-row { break-inside: avoid; page-break-inside: avoid; }
        thead { display: table-header-group; }
      }
    `}</style>
  );
}

// ── PIN screen ────────────────────────────────────────────────────────────────
function PinScreen({
  pin,
  setPin,
  onVerify,
  error,
  locked,
  lockCountdown,
  verifying,
}: {
  pin: string;
  setPin: (v: string) => void;
  onVerify: () => void;
  error: string | null;
  locked: boolean;
  lockCountdown: number;
  verifying: boolean;
}) {
  // No masked phone hint here. Showing one would mean fetching the invoice
  // before the visitor proves anything — precisely the leak this screen exists
  // to prevent. Nothing about the invoice is known until the server accepts
  // the digits.
  const formatCountdown = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  const setDigit = (i: number, val: string) => {
    const next = pin.padEnd(4, ' ').split('');
    next[i] = val || ' ';
    setPin(next.join('').replace(/ +$/, '').replace(/ /g, ''));
  };

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-xl bg-primary/10 text-primary flex items-center justify-center mx-auto mb-4">
            <Lock size={24} />
          </div>
          <h1 className="text-headline font-display font-semibold text-ink">
            Buka invoice Anda
          </h1>
          <p className="text-body text-ink-subtle mt-1">
            Demi keamanan, invoice hanya bisa dibuka oleh pemiliknya.
          </p>
        </div>

        {locked ? (
          <div className="bg-danger/5 border border-danger/20 rounded-lg p-5 text-center space-y-2">
            <AlertCircle size={28} className="mx-auto text-danger" />
            <p className="text-body font-medium text-danger">Terlalu banyak percobaan salah</p>
            <p className="text-small text-danger/80">
              Coba lagi dalam{' '}
              <strong className="font-mono">{formatCountdown(lockCountdown)}</strong>
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-small text-ink-subtle mb-3 text-center">
                Masukkan <b className="text-ink">4 digit terakhir</b> nomor HP yang terdaftar
              </p>
              <div className="flex justify-center gap-3">
                {[0, 1, 2, 3].map((i) => (
                  <input
                    key={i}
                    type="tel"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    autoFocus={i === 0}
                    value={pin[i] ?? ''}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(-1);
                      setDigit(i, val);
                      if (val && i < 3) {
                        const inputs =
                          document.querySelectorAll<HTMLInputElement>('.pin-input');
                        inputs[i + 1]?.focus();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onVerify();
                      if (e.key === 'Backspace' && !pin[i] && i > 0) {
                        const inputs =
                          document.querySelectorAll<HTMLInputElement>('.pin-input');
                        inputs[i - 1]?.focus();
                      }
                    }}
                    className={`pin-input w-12 h-14 rounded-lg border-2 bg-surface text-center text-2xl font-display font-bold text-ink transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40 ${
                      pin[i] ? 'border-primary' : 'border-line'
                    }`}
                    aria-label={`Digit ${i + 1}`}
                  />
                ))}
              </div>
            </div>

            {error && (
              <div
                role="alert"
                className="flex items-center gap-2 p-3 rounded bg-danger/5 border border-danger/20 text-danger text-small"
              >
                <AlertCircle size={15} className="shrink-0" />
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={onVerify}
              disabled={pin.length !== 4 || verifying}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-5 py-3 text-body font-medium text-white transition-colors hover:bg-primary/90 disabled:bg-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              {verifying ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Memeriksa…
                </>
              ) : (
                'Buka invoice'
              )}
            </button>
          </div>
        )}

        <p className="flex items-center justify-center gap-1.5 text-center text-caption text-ink-subtle/70 mt-6">
          <ShieldCheck size={12} />
          Halaman resmi — data invoice tidak dimuat sebelum diverifikasi.
        </p>
      </div>
    </div>
  );
}

function NotFound({ message, onBack }: { message: string; onBack: () => void }) {
  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center p-4">
      <div className="text-center space-y-3 max-w-sm">
        <Building2 size={40} className="mx-auto text-ink-subtle" />
        <h1 className="text-headline font-display font-semibold text-ink">
          Invoice tidak ditemukan
        </h1>
        <p className="text-body text-ink-subtle">{message}</p>
        <button onClick={onBack} className="mt-4 text-small text-primary hover:underline">
          ← Login ke aplikasi
        </button>
      </div>
    </div>
  );
}
